import React, { FC } from 'react';
import { Button } from '@milanote/roma';

import IconDark from '../../../components/icons/dark/IconDark';
import { useSelector } from 'react-redux';
import { selectIsMobileAppMode, selectIsTabletFormFactor } from '../../../platform/platformSelector';
import { currentBoardIdSelector, getIsCurrentVisibleBoardFetching } from '../../../element/board/boardSelector';
import { isGuestSelector } from '../../../user/currentUserSelector';
import useLockScreenOrientationPortrait from '../../../platform/screenOrientation/useLockScreenOrientationPortrait';
import { useTranslator } from '../../../components/i18n/useTranslator';
import splashLoadingStateDecorator from './splashLoadingStateDecorator';
import useAuthLocaleManager from '../../localisation/useAuthLocaleManager';

// Types
import { ImmutableMap } from '../../../../common/utils/immutableHelper';

import './Splash.scss';

type SplashProps = {
    location: Location;
};

const VALID_SPLASH_PATHNAMES = new Set(['/', '/login', '/register', '/forgotten-password']);

export const selectIsSplashVisible = (state: ImmutableMap, location: Location) => {
    // only displayed on mobile and tablet
    if (!selectIsMobileAppMode(state) && !selectIsTabletFormFactor(state)) return false;

    // only displayed when logged out
    if (!isGuestSelector(state)) return false;

    // only shown when we're not on a board
    if (currentBoardIdSelector(state)) return false;
    if (getIsCurrentVisibleBoardFetching(state)) return false;

    return VALID_SPLASH_PATHNAMES.has(location.pathname);
};

const SplashContent: FC<SplashProps> = ({ location }) => {
    const { hasResolvedLocale } = useAuthLocaleManager({ location });
    const translate = useTranslator('auth', 'splash');

    if (!hasResolvedLocale) return null;

    const subheading = translate('subheading', undefined, { html: true });

    return (
        <div className="AppSplash">
            <div className="mascot" />
            <div className="logo-section">
                <div className="logo flex flex-col items-center">
                    <IconDark name="milanote-logo-small" />
                </div>
                <h1 className="heading">{translate('heading')}</h1>
            </div>
            <h2 className="subheading">{subheading}</h2>
            <div className="spacer" />
            <div className="buttons flex flex-col gap-3 w-full px-6">
                <Button href="/login" routerOptions={{ keepParams: true }} color="primary" className="w-full" size="md">
                    {translate('loginButton')}
                </Button>
                <Button
                    href="/register"
                    routerOptions={{ keepParams: true }}
                    color="secondary"
                    className="w-full"
                    size="md"
                >
                    {translate('signUpButton')}
                </Button>
            </div>
        </div>
    );
};

const Splash: FC<SplashProps> = ({ location }) => {
    const isSplashVisible = useSelector((state: ImmutableMap) => selectIsSplashVisible(state, location));

    useLockScreenOrientationPortrait(isSplashVisible);

    if (!isSplashVisible) return null;

    return <SplashContent location={location} />;
};

export default splashLoadingStateDecorator(Splash);
